'use client'

import { useEffect, useRef } from 'react'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $getRoot, $getSelection, $isRangeSelection, $isParagraphNode, SELECTION_CHANGE_COMMAND, COMMAND_PRIORITY_LOW } from 'lexical'
import { $isMentionNode } from './MentionNode'
import { serializeEditorState } from './SerializationPlugin'
import { $getFullContextAtCursor } from './editorContext'

function mentionEndPos(serialized, start) {
    let depth = 0
    for (let i = start; i < serialized.length; i++) {
        if (serialized[i] === '{') depth++
        else if (serialized[i] === '}') { depth--; if (depth === 0) return i + 1 }
    }
    return serialized.length
}

function childLen(child, serialized, pos) {
    return $isMentionNode(child)
        ? mentionEndPos(serialized, pos) - pos
        : child.getTextContent().length
}

function $getCaretIndex(editor) {
    const serialized = serializeEditorState(editor.getEditorState())

    const selection = $getSelection()
    if (!$isRangeSelection(selection)) return null

    const anchor = selection.anchor
    const anchorNode = anchor.getNode()
    const anchorOffset = anchor.offset
    const anchorKey = anchorNode.getKey()

    const root = $getRoot()
    const paragraphs = root.getChildren().filter($isParagraphNode)

    let pos = 0
    for (let p = 0; p < paragraphs.length; p++) {
        if (p > 0) pos += 1
        const children = paragraphs[p].getChildren()

        if ($isParagraphNode(anchorNode) && paragraphs[p].getKey() === anchorKey) {
            for (let c = 0; c < Math.min(anchorOffset, children.length); c++) {
                pos += childLen(children[c], serialized, pos)
            }
            return pos
        }

        for (const child of children) {
            if (child.getKey() === anchorKey) {
                return pos + anchorOffset
            }
            pos += childLen(child, serialized, pos)
        }
    }

    return pos
}

function contextEqual(a, b) {
    if (a === b) return true
    if (!a || !b) return false
    const keys = ['segment', 'scene', 'description', 'character']
    for (const k of keys) {
        const av = a[k]
        const bv = b[k]
        if (av === bv) continue
        if (!av || !bv) return false
        if (av.id !== bv.id || av.name !== bv.name) return false
    }
    return a.mode === b.mode
}

export default function CursorContextPlugin({ onContextChange, onCaretIndexChange }) {
    const [editor] = useLexicalComposerContext()
    const prevContextRef = useRef(null)
    const prevCaretRef = useRef(null)

    useEffect(() => {
        const update = () => {
            editor.getEditorState().read(() => {
                const context = $getFullContextAtCursor()
                if (!contextEqual(context, prevContextRef.current)) {
                    prevContextRef.current = context
                    onContextChange?.(context)
                }

                const caretIndex = $getCaretIndex(editor)
                if (caretIndex !== prevCaretRef.current) {
                    prevCaretRef.current = caretIndex
                    onCaretIndexChange?.(caretIndex)
                }
            })
        }

        const unregisterUpdate = editor.registerUpdateListener(update)
        const unregisterSelection = editor.registerCommand(
            SELECTION_CHANGE_COMMAND,
            () => { update(); return false },
            COMMAND_PRIORITY_LOW,
        )

        return () => {
            unregisterUpdate()
            unregisterSelection()
        }
    }, [editor, onContextChange, onCaretIndexChange])

    return null
}
